/**
 * ModularInformantDashboard.js
 *
 * Service-user dashboard for Adult Autism informants. Renders the same visual
 * layout as the ADHD InformantDashboard (PatientHeader + QuestionnaireAccordion
 * timeline) but pulls data from ModularInformantSelfDataContext and resolves
 * {{patientFirstName}} tokens in form titles/subtitles.
 *
 * Uses the same synthetic patientDataModel adapter as ModularPatientDashboard
 * to satisfy the accordion's property reads without modifications to shared
 * components. Passes isInformant=true to skip the informant gate card.
 *
 * CYP ADHD guardians (parents/carers) additionally get:
 *   - a school-representative nomination gate before their forms unlock, and
 *   - once nomination is resolved, a header-row "School representative" button
 *     that opens the management dialog (nominated) or the nomination form
 *     (skipped), matching the Adult ADHD "Manage Informants" pattern.
 */
import React, { useContext, useEffect, useMemo, useState, useRef } from 'react';
import { ModularInformantSelfDataContext } from '../../../context/ModularInformantSelfDataContext';
import { FormTemplateProvider } from '../../../context/FormTemplateContext';
import { useAuth } from '../../../hooks/useAuth';
import { getInformantFormKeys } from '../../../constants/informantFormKeys';
import { resolveFormInstanceForDisplay } from '../../../utils/resolveFormTemplates';
import {
  mapFormInstancesToAssessments,
  computeNextQuestionnaireIndex,
  buildSyntheticPatientDataModel,
  groupAutismAssessments,
  AUTISM_INFORMANT_FORM_GROUPS,
} from '../../../utils/modularAssessmentMapper';
import PatientHeader from './components/PatientHeader';
import QuestionnaireAccordion from './components/QuestionnaireAccordion';
import PatientSettings from './components/PatientSettings';
import AllFormsCompletedFeedbackModal from '../3_forms/components/AllFormsCompletedFeedbackModal';
import CypSchoolRepNominationForm from './components/CypSchoolRepNominationForm';
import CypSchoolRepManagementDialog from './components/CypSchoolRepManagementDialog';
import useCypSchoolRepStatus from '../../../hooks/useCypSchoolRepStatus';

function ModularInformantDashboardContent() {
  const {
    header,
    formInstances,
    patientFirstName,
    loading,
    fetchInformantData,
    linkedPatientId,
    formsLocked,
  } = useContext(ModularInformantSelfDataContext);
  const { claims } = useAuth();

  const [isRefreshing, setIsRefreshing] = useState(true);
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [showNominationDialog, setShowNominationDialog] = useState(false);
  const [nominationStartsInSkipMode, setNominationStartsInSkipMode] = useState(false);
  const [showSchoolRepDialog, setShowSchoolRepDialog] = useState(false);

  const scrollContainerRef = useRef(null);
  const nextQuestionnaireRef = useRef(null);

  const userOrganisation = header?.organisation || claims?.organisation || '';

  // Refresh on mount for latest Firestore state. Only mark refresh complete
  // when fetchInformantData actually performed a fetch (not a no-op due to
  // enabled=false during claims propagation).
  useEffect(() => {
    let cancelled = false;
    setIsRefreshing(true);
    const result = fetchInformantData();
    if (result && typeof result.finally === 'function') {
      result.finally(() => {
        if (!cancelled) setIsRefreshing(false);
      });
    } else {
      setIsRefreshing(false);
    }
    return () => { cancelled = true; };
  }, [fetchInformantData]);

  // Filter to assigned informant forms and resolve display tokens
  // (replaces {{patientFirstName}} in titles/subtitles with the patient's name).
  //
  // The key list is resolved from the informant's pathway and role rather than
  // a fixed autism list: a CYP ADHD guardian has eleven forms, a school rep has
  // three, and none of them share the autism form keys. Iterating a hardcoded
  // autism list here would render an empty dashboard for every CYP informant.
  const assignedFormKeys = useMemo(
    () => getInformantFormKeys(claims?.assessmentModule, claims?.informantRole),
    [claims?.assessmentModule, claims?.informantRole],
  );

  const resolvedFormInstances = useMemo(() => {
    const resolved = {};
    assignedFormKeys.forEach(key => {
      if (formInstances[key]) {
        resolved[key] = resolveFormInstanceForDisplay(formInstances[key], patientFirstName);
      }
    });
    return resolved;
  }, [formInstances, patientFirstName, assignedFormKeys]);

  // Build assessment objects for the accordion from resolved form instances
  const assessments = useMemo(
    () => mapFormInstancesToAssessments(resolvedFormInstances, '/informant/form'),
    [resolvedFormInstances]
  );

  // Group flat assessments into thematic accordion sections with third-person
  // labels (Their Health and Wellbeing, Their Childhood, Their Adult Life)
  const groupedAssessments = useMemo(
    () => groupAutismAssessments(assessments, AUTISM_INFORMANT_FORM_GROUPS),
    [assessments]
  );

  // Synthetic patientDataModel for time estimates. Since informants don't need
  // the informant gate, we force hasCompletedInformantSetupForm to true.
  const syntheticPatientDataModel = useMemo(() => {
    const model = buildSyntheticPatientDataModel(header, resolvedFormInstances);
    return { ...model, hasCompletedInformantSetupForm: true };
  }, [header, resolvedFormInstances]);

  // Index of next incomplete form for auto-expand and violet pulse
  const nextIdx = useMemo(
    () => computeNextQuestionnaireIndex(groupedAssessments),
    [groupedAssessments]
  );

  // ── CYP ADHD school-rep nomination gate (hard gate, product decision) ────
  // The responsible parent/carer must nominate the school representative (or
  // skip with a reason) before their own questionnaires unlock. Resolution
  // is read from the guardian's own profile: schoolRepNomination is stamped
  // server-side on nomination and written client-side on skip. Only CYP
  // guardians are gated — school reps and autism informants never see this.
  const isCypGuardian =
    claims?.assessmentModule === 'cypAdhd' && claims?.informantRole === 'legalGuardian';
  const nominationResolved = Boolean(header?.schoolRepNomination?.status);
  // Gate strictness is stamped on the guardian's own profile at creation
  // (org policy cypAdhdFlow.schoolRepNomination): under 'requiredWhenInSchool'
  // (TOC) the only way past without nominating is confirming the child is not
  // in school — the escape link says exactly that, not a generic "unable".
  const nominationRequired = header?.schoolRepNominationPolicy === 'requiredWhenInSchool';
  const nominationGate = useMemo(() => {
    if (!isCypGuardian || nominationResolved) return null;
    const childName = patientFirstName || '';
    return {
      title: 'Nominate a school representative',
      body: childName
        ? `Before you start your questionnaires, please nominate someone from ${childName}'s school — like their SENCO or class teacher. They will be asked to answer a few questionnaires about how ${childName} is getting on at school.`
        : 'Before you start your questionnaires, please nominate someone from the school — like the SENCO or class teacher. They will be asked to answer a few questionnaires about how your child is getting on at school.',
      ctaLabel: 'Nominate school representative',
      onCta: () => {
        setNominationStartsInSkipMode(false);
        setShowNominationDialog(true);
      },
      secondaryLabel: nominationRequired
        ? `${childName || 'My child'} is not in school, nursery or college`
        : "I'm unable to provide these details",
      onSecondary: () => {
        setNominationStartsInSkipMode(true);
        setShowNominationDialog(true);
      },
    };
  }, [isCypGuardian, nominationResolved, nominationRequired, patientFirstName]);

  // ── CYP ADHD school-rep status (post-nomination) ─────────────────────────
  // The school rep's details come from the getCypSchoolRepStatus callable
  // because Firestore rules stop a guardian reading another informant's
  // profile. We only fire it for a CYP guardian whose nomination is
  // 'nominated' — a skipped guardian sees no rep data (they get the
  // nomination form via the header button). The hook settles to an empty
  // state the moment `enabled` is false, so a guardian who just skipped
  // never sees stale data.
  const nominationStatus = header?.schoolRepNomination?.status || null;
  const schoolRepQueryEnabled =
    isCypGuardian && nominationStatus === 'nominated' && Boolean(linkedPatientId);
  const {
    status: schoolRepStatus,
    schoolRep,
    loading: schoolRepLoading,
    error: schoolRepError,
    refresh: refreshSchoolRep,
  } = useCypSchoolRepStatus({ enabled: schoolRepQueryEnabled });
  // The header button is the single entry point for school-rep management,
  // matching Adult ADHD where the header "Manage Informants" button is always
  // visible after setup. For CYP guardians it appears once nomination is
  // resolved (nominated or skipped), covering both "manage the rep" and
  // "add a rep you previously skipped."
  const showSchoolRepHeaderButton = isCypGuardian && nominationResolved;

  // Auto-scroll to next incomplete questionnaire once data is ready
  const [isDataLoaded, setIsDataLoaded] = useState(false);

  useEffect(() => {
    if (!isRefreshing && assessments.length > 0) {
      setIsDataLoaded(true);
    }
  }, [isRefreshing, assessments]);

  // Show all-forms-completed feedback modal when every assigned form is done
  // and the informant hasn't already submitted feedback. Mirrors the exact
  // pattern used in ModularPatientDashboard for consistency.
  const shouldShowFeedbackModal = useMemo(() => {
    if (!formInstances || Object.keys(formInstances).length === 0) return false;
    const allCompleted = Object.values(formInstances).every(fi => fi?.status === 'completed');
    const feedbackAlreadySubmitted = header?.formFeedback?.submittedAt;
    return allCompleted && !feedbackAlreadySubmitted;
  }, [formInstances, header]);

  useEffect(() => {
    if (shouldShowFeedbackModal && isDataLoaded) {
      const timer = setTimeout(() => setShowFeedbackModal(true), 1000);
      return () => clearTimeout(timer);
    }
  }, [shouldShowFeedbackModal, isDataLoaded]);

  useEffect(() => {
    if (!isDataLoaded || !scrollContainerRef.current) return;

    const scrollTimeout = setTimeout(() => {
      if (nextQuestionnaireRef.current && scrollContainerRef.current) {
        const targetRect = nextQuestionnaireRef.current.getBoundingClientRect();
        const containerRect = scrollContainerRef.current.getBoundingClientRect();
        const scrollOffset = 16;
        const scrollPosition = scrollContainerRef.current.scrollTop + targetRect.top - containerRect.top - scrollOffset;

        scrollContainerRef.current.scrollTo({
          top: Math.max(0, scrollPosition),
          behavior: 'smooth',
        });
      }
    }, 300);

    return () => clearTimeout(scrollTimeout);
  }, [isDataLoaded, nextIdx]);

  // "Assignment pending" empty state when patient link is not yet established
  if (!loading && !isRefreshing && !linkedPatientId) {
    return (
      <div className="h-screen bg-white flex flex-col">
        <div className="flex-shrink-0">
          <PatientHeader
            useModularHeader
            isInformant
            onSettingsClick={() => setIsSettingsOpen(true)}
          />
        </div>
        <div className="flex-1 flex items-center justify-center px-4">
          <div className="text-center max-w-md">
            <p className="text-gray-500">
              Your assignment is being set up. Please check back shortly or contact
              the clinic if this message persists.
            </p>
          </div>
        </div>
        <PatientSettings isOpen={isSettingsOpen} onClose={() => setIsSettingsOpen(false)} />
      </div>
    );
  }

  return (
    <div className="h-screen bg-white flex flex-col">
      {/* Fixed Header */}
      <div className="flex-shrink-0 relative z-10">
        <PatientHeader
          useModularHeader
          isInformant
          assessments={assessments}
          onSettingsClick={() => setIsSettingsOpen(true)}
          onSchoolRepClick={showSchoolRepHeaderButton ? () => {
            if (nominationStatus === 'nominated') {
              setShowSchoolRepDialog(true);
            } else {
              // Skipped: open the nomination form so the guardian can add
              // a school rep they previously chose not to nominate — same
              // pattern as Adult ADHD "Add informant" from the manage dialog.
              setNominationStartsInSkipMode(false);
              setShowNominationDialog(true);
            }
          } : undefined}
        />
        <div className="absolute bottom-0 left-0 right-0 h-4 pointer-events-none transform translate-y-full z-20">
          <div className="absolute inset-0 bg-gradient-to-b from-white/80 via-white/60 to-transparent"></div>
        </div>
      </div>

      {/* Scrollable Content Area */}
      <div ref={scrollContainerRef} className="flex-1 overflow-y-auto relative z-0">
        {/* Skeleton while loading */}
        <div className={`px-4 pt-4 space-y-3 transition-opacity duration-300 ease-in-out ${
          isRefreshing ? 'opacity-100' : 'opacity-0 h-0 overflow-hidden pointer-events-none'
        }`}>
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm animate-pulse">
              <div className="flex items-center justify-between">
                <div className="space-y-2 flex-1">
                  <div className="h-4 w-2/5 rounded bg-gray-200" />
                  <div className="h-3 w-1/3 rounded bg-gray-100" />
                </div>
                <div className="h-8 w-8 rounded-full bg-gray-200" />
              </div>
              <div className="mt-3 h-2 w-full rounded-full bg-gray-100" />
            </div>
          ))}
        </div>

        {/* Lock notice — shown when the clinician has generated a CI or SSI
            report and the informant's forms are now read-only. Positioned above
            the accordion so it is the first thing the informant sees. */}
        {!isRefreshing && formsLocked && (
          <div className="mx-4 mt-4 rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-800">
            <p className="font-semibold">Your questionnaires are now locked</p>
            <p className="mt-1">
              {patientFirstName
                ? `${patientFirstName}'s assessment is underway so your answers can no longer be changed. You can still view your previous answers. If you need to make changes, please contact the clinical team.`
                : 'The assessment is underway so your answers can no longer be changed. You can still view your previous answers. If you need to make changes, please contact the clinical team.'}
            </p>
          </div>
        )}

        {/* Questionnaire accordion */}
        {!isRefreshing && (
          <div className="animate-fadeIn">
            <QuestionnaireAccordion
              assessments={groupedAssessments}
              patientDataModel={syntheticPatientDataModel}
              nextQuestionnaireIndex={nextIdx}
              nextQuestionnaireRef={nextQuestionnaireRef}
              isInformant={true}
              userOrganisation={userOrganisation}
              nominationGate={nominationGate}
            />
          </div>
        )}
      </div>

      <PatientSettings isOpen={isSettingsOpen} onClose={() => setIsSettingsOpen(false)} />
      <CypSchoolRepNominationForm
        isOpen={showNominationDialog}
        onClose={() => setShowNominationDialog(false)}
        startInSkipMode={nominationStartsInSkipMode}
      />
      {isCypGuardian && (
        <CypSchoolRepManagementDialog
          isOpen={showSchoolRepDialog}
          onClose={() => setShowSchoolRepDialog(false)}
          schoolRep={schoolRep}
          patientId={linkedPatientId}
          childName={patientFirstName}
          onRefresh={refreshSchoolRep}
        />
      )}
      <AllFormsCompletedFeedbackModal
        isOpen={showFeedbackModal}
        onClose={() => setShowFeedbackModal(false)}
        onFeedbackSubmitted={() => fetchInformantData()}
        feedbackCollection="informants"
        subjectPatientId={linkedPatientId}
      />
    </div>
  );
}

export default function ModularInformantDashboard() {
  const { patientFirstName } = useContext(ModularInformantSelfDataContext);

  return (
    <FormTemplateProvider patientFirstName={patientFirstName}>
      <ModularInformantDashboardContent />
    </FormTemplateProvider>
  );
}
